/**
 * Login Page - Split layout with hero image
 * Supports "Remember Role" feature for multi-role users
 */
import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Loader2, Link2, Eye, EyeOff, Fingerprint } from "lucide-react";
import { useWebAuthn, isWebAuthnSupported } from "../hooks/useWebAuthn";
import { useLanguage } from "../i18n/LanguageContext";
import api from "../lib/api";
import { getRememberedRole, clearRememberedRole } from "./RoleSelector";

// Role to path mapping
const ROLE_PATHS = {
  ADMIN: "/admin",
  CEO: "/admin",
  STAFF: "/admin",
  DRIVER: "/driver",
  BUSINESS_MERCHANT: "/merchant",
  PUDO_POINT: "/pudo",
  PUDO_OPERATOR: "/pudo",
  FULFILLMENT: "/fulfillment",
  CUSTOMER: "/dashboard"
};

// Get subdomain from hostname or URL param
const getSubdomain = () => {
  const hostname = window.location.hostname;
  if (hostname.startsWith("admin.")) return "admin";
  if (hostname.startsWith("fulfillment.")) return "fulfillment";
  if (hostname.startsWith("go.")) return "go";
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get("subdomain");
};

const Login = () => {
  const { t } = useLanguage();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loginError, setLoginError] = useState("");
  const { login, updateUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { loginWithPasskey, isLoading: passkeyLoading, isSupported: passkeySupported } = useWebAuthn();
  const [showPasskeyOption, setShowPasskeyOption] = useState(false);

  const handlePasskeyLogin = async () => {
    if (!identifier) {
      toast.error("Please enter your email first");
      return;
    }
    const result = await loginWithPasskey(identifier);
    if (result) {
      localStorage.setItem("token", result.access_token);
      const user = result.user;
      if (updateUser) updateUser(user);
      toast.success("Welcome back!");
      
      if (user.hasMultipleRoles && user.roles?.length > 1) {
        const pendingRedirect = searchParams.get("redirect"); if (pendingRedirect) { sessionStorage.setItem("postLoginRedirect", pendingRedirect); } navigate("/select-role", { replace: true });
      } else {
        const path = user.roles?.includes("DRIVER") ? "/driver" :
                     user.roles?.includes("BUSINESS_MERCHANT") ? "/merchant" :
                     user.roles?.includes("PUDO_POINT") ? "/pudo" :
                     user.roles?.includes("ADMIN") ? "/admin" : "/dashboard";
        navigate(path, { replace: true });
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setLoginError("");

    // Store redirect URL in sessionStorage so it survives auth state changes
    const redirectParam = searchParams.get("redirect");
    if (redirectParam) {
      sessionStorage.setItem("post_login_redirect", redirectParam);
    }

    try {
      const user = await login(identifier, password);
      
      // Check if user must change password (first-time login for staff)
      if (user.must_change_password) {
        toast.info("Please set up your new password and PIN");
        navigate("/first-time-setup", { replace: true });
        return;
      }
      
      toast.success(t("auth.welcomeBack") || "Welcome back!");
      
      // Check for redirect param first (query param + sessionStorage)
      const redirectUrl = searchParams.get("redirect") || sessionStorage.getItem("post_login_redirect");
      console.log("[LOGIN] redirect check:", { searchParam: searchParams.get("redirect"), sessionStorage: sessionStorage.getItem("post_login_redirect"), redirectUrl });
      if (redirectUrl) {
        sessionStorage.removeItem("post_login_redirect");
        console.log("[LOGIN] Hard-redirecting to:", redirectUrl);
        window.location.href = redirectUrl;
        return;
      }
      
      // Check if user has multiple roles
      if (user.hasMultipleRoles && user.roles?.length > 1) {
        // Check for remembered role preference
        const remembered = getRememberedRole();
        if (remembered && remembered.userId === user.id && user.roles.includes(remembered.role)) {
          // Auto-switch to remembered role
          try {
            const response = await api.post("/auth/switch-role", { role: remembered.role });
            localStorage.setItem("token", response.data.access_token);
            
            if (updateUser) {
              updateUser({ ...user, activeRole: remembered.role });
            }
            
            // Check for redirect param - takes priority over role path
            const pendingRedirectAfterSwitch = searchParams.get("redirect");
            if (pendingRedirectAfterSwitch) {
              navigate(pendingRedirectAfterSwitch, { replace: true });
            } else {
              const path = ROLE_PATHS[remembered.role] || "/dashboard";
              navigate(path, { replace: true });
            }
            return;
          } catch (switchError) {
            console.error("Error auto-switching role:", switchError);
            // Clear invalid preference and show role selector
            clearRememberedRole();
          }
        }
        
        // No remembered role or it failed - show role selector
        const pendingRedirect = searchParams.get("redirect"); if (pendingRedirect) { sessionStorage.setItem("postLoginRedirect", pendingRedirect); } navigate("/select-role", { replace: true });
        return;
      }
      
      // Single role - redirect based on subdomain or role
      const subdomain = getSubdomain();
      // Check redirect param before subdomain routing
      const singleRoleRedirect = searchParams.get("redirect");
      if (singleRoleRedirect) {
        navigate(singleRoleRedirect, { replace: true });
      } else if (subdomain === "go") {
        // go.liantiaoja.com - redirect to pilot bulky/hire booking
        navigate("/pilot/bulky", { replace: true });
      } else if (subdomain === "admin" && (user.roles?.includes("ADMIN") || user.roles?.includes("CEO") || user.roles?.includes("STAFF"))) {
        navigate("/admin", { replace: true });
      } else if (subdomain === "fulfillment") {
        navigate("/pudo", { replace: true });
      } else if (user.roles?.includes("STAFF")) {
        navigate("/admin", { replace: true });
      } else if (user.roles?.includes("DRIVER")) {
        navigate("/driver", { replace: true });
      } else if (user.roles?.includes("BUSINESS_MERCHANT")) {
        navigate("/merchant", { replace: true });
      } else if (user.roles?.includes("PUDO_POINT") || user.roles?.includes("PUDO_OPERATOR")) {
        navigate("/pudo", { replace: true });
      } else {
        navigate("/dashboard", { replace: true });
      }
    } catch (error) {
      const detail = error.response?.data?.detail || error.response?.data?.error?.message || "";
      let message = "Login failed. Please check your credentials.";
      if (detail.includes("Invalid credentials")) {
        message = "Incorrect email or password. If you received new credentials via email, please use those. You can also use 'Forgot password?' to reset.";
      } else if (detail.includes("deactivated")) {
        message = "Your account has been deactivated. Please contact support.";
      } else if (detail) {
        message = detail;
      }
      setLoginError(message);
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 min-h-screen w-full overflow-hidden bg-slate-950">
      {/* Left Side - Form */}
      <div className="flex flex-col justify-center px-8 md:px-16 lg:px-20 py-12">
        <div className="max-w-md w-full mx-auto">
          {/* Logo */}
          <div className="mb-12">
            <div className="flex items-center gap-3 mb-2">
              <Link2 className="h-8 w-8 text-amber-500" />
              <span className="text-2xl font-bold text-white tracking-tight font-['Manrope']">
                Liantiao
              </span>
            </div>
            <p className="text-slate-400 text-sm font-['Manrope']">The Chain - Jamaica's Logistics Network</p>
          </div>

          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl md:text-4xl font-bold text-white tracking-tight font-['Manrope'] mb-2">
              Welcome back
            </h1>
            <p className="text-slate-400 font-['Manrope']">
              Sign in to access your dashboard
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="identifier" className="text-slate-300 font-['Manrope']">
                Email or Phone
              </Label>
              <Input
                id="identifier"
                data-testid="login-identifier-input"
                type="text"
                placeholder="email@example.com"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                className="bg-slate-950 border-slate-800 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 rounded-sm h-11 text-white placeholder:text-slate-600"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-300 font-['Manrope']">
                Password
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  data-testid="login-password-input"
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="bg-slate-950 border-slate-800 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 rounded-sm h-11 text-white placeholder:text-slate-600 pr-10"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
                  data-testid="toggle-password-visibility"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            <div className="flex justify-end mt-1">
              <Link to="/forgot-password" className="text-sm text-amber-500 hover:text-amber-400">Forgot password?</Link>
            {loginError && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mt-2" data-testid="login-error-msg">
                <p className="text-red-400 text-sm">{loginError}</p>
              </div>
            )}
            </div>

            <Button
              type="submit"
              data-testid="login-submit-btn"
              disabled={isLoading}
              className="w-full bg-amber-500 text-slate-950 hover:bg-amber-400 rounded-sm h-11 font-medium tracking-wide transition-all active:scale-95 shadow-[0_0_15px_rgba(245,158,11,0.3)]"
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Signing in...
                </>
              ) : (
                "Sign in"
              )}
            </Button>
          </form>

          {/* Passkey Login Option */}
          {passkeySupported && (
            <div className="mt-4">
              <div className="relative flex items-center justify-center my-4">
                <div className="border-t border-slate-800 w-full"></div>
                <span className="bg-slate-950 px-4 text-sm text-slate-500 absolute">or</span>
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={handlePasskeyLogin}
                disabled={passkeyLoading || !identifier}
                className="w-full border-slate-700 text-slate-300 hover:bg-slate-800 rounded-sm h-11"
                data-testid="passkey-login-btn"
              >
                {passkeyLoading ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Fingerprint className="mr-2 h-4 w-4" />
                )}
                Sign in with Passkey
              </Button>
              <p className="text-xs text-slate-500 text-center mt-2">
                Use fingerprint or face recognition
              </p>
            </div>
          )}

          {/* Register Link */}
          <p className="mt-8 text-center text-slate-400 font-['Manrope']">
            Don't have an account?{" "}
            <Link 
              to="/register" 
              data-testid="register-link"
              className="text-amber-500 hover:text-amber-400 transition-colors"
            >
              Create account
            </Link>
          </p>
        </div>
      </div>

      {/* Right Side - Hero Image */}
      <div className="hidden lg:block relative">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: "url(https://images.unsplash.com/photo-1751380911352-c216693fb27b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHw0fHxhZXJpYWwlMjB2aWV3JTIwY29udGFpbmVyJTIwcG9ydCUyMG5pZ2h0JTIwbGlnaHRzfGVufDB8fHx8MTc2NTg2MTg5MXww&ixlib=rb-4.1.0&q=85)"
          }}
        />
        <div className="absolute inset-0 bg-slate-950/60" />
        <div className="absolute bottom-12 left-12 right-12">
          <blockquote className="text-white font-['Manrope']">
            <p className="text-xl md:text-2xl font-medium mb-4 leading-relaxed">
              "Connecting businesses, drivers, and customers across Jamaica."
            </p>
            <footer className="text-slate-400 text-sm">
              <span className="font-mono text-amber-500">LIANTIAO</span> — Enterprise Logistics Platform
            </footer>
          </blockquote>
        </div>
      </div>
    </div>
  );
};

export default Login;
